import React, { useCallback, useEffect, useState } from "react";
import Styles from './styles.module.scss';
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    login,
    loginFailure,
    resetLogin2fa,
    verifyLogin2fa
} from "../../store/main/action";

import TwoFactorCodeInput from "../_UI/twoFactorCodeInput";

import logIcon from "../../theme/assets/svg/b_log_icon.svg";
import pasIcon from "../../theme/assets/svg/b_pas_icon.svg";
import showAIcon from "../../theme/assets/svg/b_pas_show_a_icon.svg";
import showPIcon from "../../theme/assets/svg/b_pas_show_p_icon.svg";
import rememberPIcon from "../../theme/assets/svg/b_remember_device_check_p_icon.svg";
import rememberAIcon from "../../theme/assets/svg/b_remember_device_check_a_icon.svg";

const CODE_LENGTH = 6;

const LogMain = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const formErr = useSelector(store => store.main.loginErr);
    const loginStarted = useSelector(store => store.main.loginStarted);

    const login2faRequired = useSelector(store => store.main.login2faRequired);
    const login2faChallengeToken = useSelector(store => store.main.login2faChallengeToken);
    const login2faExpiresIn = useSelector(store => store.main.login2faExpiresIn);
    const login2faVerifyStarted = useSelector(store => store.main.login2faVerifyStarted);
    const login2faVerifyErr = useSelector(store => store.main.login2faVerifyErr);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [isShowPassword, setIsShowPassword] = useState(false);
    const [isRememberDevice, setIsRememberDevice] = useState(false);

    const isLoading = loginStarted || login2faVerifyStarted;

    const isLoginDisabled =
        isLoading ||
        !email.trim() ||
        !password;

    const isVerifyDisabled =
        isLoading ||
        !login2faChallengeToken ||
        verificationCode.length !== CODE_LENGTH;

    const sendFormHandler = useCallback(() => {
        if (isLoading) return;

        if (login2faRequired) {
            if (isVerifyDisabled) return;

            dispatch(verifyLogin2fa({
                challenge_token: login2faChallengeToken,
                code: verificationCode,
            }));

            return;
        }

        if (isLoginDisabled) return;

        dispatch(login({
            email,
            password,
        }));
    }, [
        isLoading,
        login2faRequired,
        isVerifyDisabled,
        login2faChallengeToken,
        verificationCode,
        isLoginDisabled,
        email,
        password,
        dispatch,
    ]);

    const backToLoginHandler = () => {
        if (isLoading) return;

        setVerificationCode("");
        dispatch(resetLogin2fa());
    };

    const emailChangeHandler = (event) => {
        setEmail(event.target.value);

        if (formErr?.email) {
            dispatch(loginFailure({
                ...formErr,
                email: null,
            }));
        }
    };

    const passwordChangeHandler = (event) => {
        setPassword(event.target.value);

        if (formErr?.password) {
            dispatch(loginFailure({
                ...formErr,
                password: null,
            }));
        }
    };

    useEffect(() => {
        const onKeyDown = e => {
            if (e.keyCode === 13) {
                sendFormHandler();
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [sendFormHandler]);

    useEffect(() => {
        return () => {
            dispatch(resetLogin2fa());
        };
    }, [dispatch]);

    return (
        <section className={`${Styles.container}`}>
            <div className={`${Styles.contentWrap} ${Styles.pageContainer}`}>
                <div className={`${Styles.formWrap}`}>
                    <div className={`${Styles.formTitle}`}>
                        {login2faRequired
                            ? t('Подтвердите вход')
                            : t('Войдите в свой аккаунт')}
                    </div>

                    <div className={`${Styles.formSubTitle}`}>
                        {login2faRequired
                            ? t('Введите код из приложения-аутентификатора')
                            : t('Введите пароль, чтобы безопасно получить доступ к аккаунту NEXT')}
                    </div>

                    {!login2faRequired ? (
                        <>
                            <div className={`${Styles.contentBox}`}>
                                <div className={`${Styles.inputWrap} ${formErr?.email ? Styles.inputWrapWithErr : ""}`}>
                                    <div className={`${Styles.inputTitleRow}`}>
                                        <div className={`${Styles.inputTitle}`}>
                                            {t('Почта')}
                                        </div>
                                    </div>

                                    <div className={`${Styles.inputBox}`}>
                                        <input
                                            type="text"
                                            placeholder="example@gmail.com"
                                            value={email}
                                            onChange={emailChangeHandler}
                                            disabled={isLoading}
                                        />

                                        <div className={`${Styles.absLeft}`}>
                                            <img src={logIcon} alt={t('Почта')} />
                                        </div>
                                    </div>

                                    <div className={`${Styles.tcErr} ${formErr?.email ? Styles.tcErrActive : ""}`}>
                                        {formErr?.email ? formErr.email : null}
                                    </div>
                                </div>

                                <div className={`${Styles.inputWrap} ${formErr?.password ? Styles.inputWrapWithErr : ""}`}>
                                    <div className={`${Styles.inputTitleRow}`}>
                                        <div className={`${Styles.inputTitle}`}>
                                            {t('Пароль')}
                                        </div>

                                        <div
                                            className={`${Styles.inputTitleForgot}`}
                                            onClick={() => {
                                                if (!isLoading) {
                                                    navigate('/password/forgot');
                                                }
                                            }}
                                        >
                                            {t('Забыли пароль?')}
                                        </div>
                                    </div>

                                    <div className={`${Styles.inputBox}`}>
                                        <input
                                            type={isShowPassword ? "text" : "password"}
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={passwordChangeHandler}
                                            disabled={isLoading}
                                        />

                                        <div className={`${Styles.absLeft}`}>
                                            <img src={pasIcon} alt={t('Пароль')} />
                                        </div>

                                        <div
                                            className={`${Styles.absRight}`}
                                            onClick={() => {
                                                if (!isLoading) {
                                                    setIsShowPassword(!isShowPassword);
                                                }
                                            }}
                                        >
                                            <img src={isShowPassword ? showPIcon : showAIcon} alt={t('Пароль')} />
                                        </div>
                                    </div>

                                    <div className={`${Styles.tcErr} ${formErr?.password?.[0] ? Styles.tcErrActive : ""}`}>
                                        {formErr?.password?.[0] ? formErr.password[0] : null}
                                    </div>
                                </div>
                            </div>

                            <div className={`${Styles.rememberRow}`}>
                                <div className={`${Styles.rememberBox}`}>
                                    <div
                                        className={`${Styles.rememberBoxImgWrap}`}
                                        onClick={() => {
                                            if (!isLoading) {
                                                setIsRememberDevice(!isRememberDevice);
                                            }
                                        }}
                                    >
                                        <img
                                            src={isRememberDevice ? rememberAIcon : rememberPIcon}
                                            alt={t('Запомнить это устройство')}
                                        />
                                    </div>

                                    <div className={`${Styles.rememberBoxText}`}>
                                        {t('Запомнить это устройство')}
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className={`${Styles.contentBox} ${Styles.twoFactorContentBox}`}>
                            <div className={Styles.twoFactorCodeWrap}>
                                <TwoFactorCodeInput
                                    value={verificationCode}
                                    onChange={setVerificationCode}
                                    formErr={login2faVerifyErr}
                                    errorFieldName="code"
                                    isLoading={login2faVerifyStarted}
                                    label="Код аутентификации"
                                    hint="Откройте приложение-аутентификатор и введите текущий код."
                                />
                            </div>

                            {login2faExpiresIn && (
                                <div className={`${Styles.twoFactorHint}`}>
                                    {t('Код подтверждения действует ограниченное время')}
                                </div>
                            )}

                            <button
                                type="button"
                                className={Styles.backToLoginBtn}
                                onClick={backToLoginHandler}
                                disabled={isLoading}
                            >
                                {t('Вернуться к вводу логина и пароля')}
                            </button>
                        </div>
                    )}

                    <div
                        className={`
                            ${Styles.mainBtn}
                            ${login2faRequired
                            ? isVerifyDisabled ? Styles.mainBtnDisabled : ""
                            : isLoginDisabled ? Styles.mainBtnDisabled : ""}
                        `}
                        onClick={sendFormHandler}
                    >
                        {isLoading
                            ? t('Загрузка')
                            : login2faRequired
                                ? t('Подтвердить вход')
                                : t('Войти')}
                    </div>

                    {!login2faRequired && (
                        <>
                            <div className={`${Styles.sep}`} />

                            <div className={`${Styles.note}`}>
                                {t('Впервые в NEXT?')}{" "}
                                <span onClick={() => navigate('/sign-up')}>
                                    {t('Стать клиентом')}
                                </span>
                            </div>
                        </>
                    )}
                </div>

                <div className={`${Styles.imgWrap}`} />
            </div>
        </section>
    );
};

export default LogMain;